// /src/components/RootWrapper.tsx
import { Button, Grid, Divider, Slider, Typography } from '@mui/material';
import React from 'react';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useState } from 'react';
import { useBrandLanguage } from '@context/brand-language/context';
import { useUserMetrics, useCreatedAt } from '@features/user';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseUTCDate } from '@utils/dateUtils';
import LayoutWrapper from './layout/UILayout';

interface LockoutProps {
  user_email?: string;
  payment_status?: string;
}

export const Lockout: React.FC<LockoutProps> = ({ payment_status }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userMetrics = useUserMetrics();
  const createdAt = useCreatedAt();
  const isRecorderPage =
    location.pathname.includes('/record') || location.pathname.includes('/encounters/');
  const { logout } = useAuth0();
  const notesCreated = userMetrics?.notes_created || 0;
  const daysOfUsage = createdAt
    ? Math.ceil((Date.now() - parseUTCDate(createdAt).getTime()) / (1000 * 60 * 60 * 24))
    : 10;
  const [timeSavedSlider, setTimeSavedSlider] = useState(25);
  const [patientEncounterPerct, setPatientEncounterPerct] = useState(30);
  const { brandLanguage } = useBrandLanguage();

  const calculateHoursSaved = () => {
    const hoursSaved =
      (notesCreated * timeSavedSlider * (100 / patientEncounterPerct) * (365 / daysOfUsage)) / 60;
    return Math.round(hoursSaved);
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    // Note: userInfo is no longer stored in localStorage - it's in RTK Query cache
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  if (isRecorderPage) {
    return null; // Don't show lockout on recorder page
  }

  return (
    <Grid container spacing={2} sx={{ marginTop: 4, maxWidth: '100%' }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box
          sx={{
            minWidth: '350px',
            bgcolor: 'backgroundColors.secondary',
            border: 1,
            borderColor: 'borderColors.primary',
            borderRadius: '0.75rem',

            alignItems: 'flex-start',
            padding: '1rem 1.5rem',
            margin: '0.5rem',
            width: '100%',
          }}
        >
          {/* <h3 style={{ fontWeight: "bold" }} className="mb-4 text-lg">
            Action Required: Update Your Subscription
          </h3> */}
          {(function () {
            switch (payment_status) {
              case 'paused':
                return (
                  <>
                    <LockoutHeaderText message="Begin Your Paid Subscription" />
                    <p style={{ marginBottom: '20px' }}>
                      Hi there! 👋{' '}
                      <strong>It looks like your free trial has just wrapped up.</strong>{' '}
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      By subscribing, you'll be back to spending less time charting and more time
                      focusing on patient care, family, friends, or literally anything else. We'd
                      love to have you onboard.
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      If you can't get to this right away, you can continue to record notes with
                      your companion app. However, you won't be able to view any notes until your
                      subscription is active.
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      Click the button below to head over to our billing portal and begin your
                      subscription!
                    </p>
                    {/* <p style={{ marginBottom: "20px" }}>
                      Don't let the momentum stop! Click the button
                      below to head over to our billing portal and begin your
                      subscription!
                    </p> */}
                    <ContactUsText />
                    <SubscriptionButton
                      message={'Billing Portal'}
                      onClick={() => navigate('/practice/subscriptions')}
                    />
                    <p style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <span style={{ fontWeight: 'bold' }}>Looking to extend your trial?</span> Earn
                      10 more days by signing up for a user research interview with us.
                    </p>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ width: '100%', margin: '4px 0 20px 0' }}
                      onClick={() =>
                        window.open(
                          'https://calendly.com/d/cps8-knc-cth/jotpsych-user-research-interview',
                          '_blank',
                        )
                      }
                    >
                      Schedule user interview to extend trial
                    </Button>
                  </>
                );
              case 'unpaid':
                return (
                  <>
                    <LockoutHeaderText message="Update Your Payment Information" />
                    <p style={{ marginBottom: '20px' }}>
                      Oops! 🤔{' '}
                      <strong>
                        {' '}
                        We've noticed there's been an issue with your {brandLanguage.brandName}{' '}
                        subscription payment.
                      </strong>
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      To get back on track, simply click the billing portal button below. It's a
                      quick process to update your payment details, and then you'll be all set to
                      continue enjoying the benefits of {brandLanguage.brandName}.
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      If you can't get to this right away, you can continue to record notes with
                      your companion app. However, you won't be able to view any notes until this
                      issue is resolved.
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      You can still access the recorder to create new notes. Click the button below
                      to go to the recorder.
                    </p>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ width: '100%', margin: '4px 0 20px 0' }}
                      onClick={() => navigate('/encounters/active')}
                    >
                      Go to Recorder
                    </Button>
                    <ContactUsText />
                    <SubscriptionButton
                      message={'Billing Portal'}
                      onClick={() => navigate('/practice/subscriptions')}
                    />
                  </>
                );
              case 'canceled':
                return (
                  <>
                    <LockoutHeaderText message="Did you mean to cancel?" />
                    <p style={{ marginBottom: '20px' }}>
                      <strong>
                        It looks like your {brandLanguage.brandName} subscription has been canceled.
                      </strong>{' '}
                      😢
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      By resubscribing, you'll be back to spending less time charting and more time
                      focusing on patient care, family, friends, or literally anything else. We'd
                      love to have you back.
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      You can still access the recorder to create new notes. Click the button below
                      to go to the recorder.
                    </p>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ width: '100%', margin: '4px 0 20px 0' }}
                      onClick={() => navigate('/encounters/active')}
                    >
                      Go to Recorder
                    </Button>
                    <ContactUsText />
                    <SubscriptionButton
                      message={'Reactivate your Subscription'}
                      onClick={() => navigate('/practice/subscriptions')}
                    />
                  </>
                );
              default:
                return (
                  <>
                    <LockoutHeaderText message="Please Contact Our Team" />
                    <p style={{ marginBottom: '20px' }}>
                      <strong>
                        There has been a technical issue with your {brandLanguage.brandName} account
                        or payment.
                      </strong>{' '}
                      🤔
                    </p>
                    <div style={{ marginBottom: '20px' }}>
                      Please{' '}
                      <a
                        href={`https://${brandLanguage.brandSite}/contact`}
                        target="_blank"
                        className="text-blue-500"
                      >
                        get in contact with us{' '}
                      </a>
                      as soon as possible.
                    </div>
                  </>
                );
            }
          })()}
        </Box>

        <div style={{ alignSelf: 'flex-end', marginTop: 'auto' }}>
          <a
            href="#"
            onClick={handleLogout}
            style={{
              textDecoration: 'underline', // Makes it look like a link
              fontSize: '14px', // Smaller text
              cursor: 'pointer', // Changes cursor to pointer on hover
            }}
          >
            Logout of {brandLanguage.brandName}
          </a>
        </div>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Box
          sx={{
            minWidth: '350px',
            bgcolor: 'backgroundColors.secondary',
            border: 1,
            borderColor: 'borderColors.primary',
            borderRadius: '0.75rem',

            alignItems: 'flex-start',
            padding: '1rem 1.5rem',
            margin: '0.5rem',
            width: '100%',
          }}
        >
          <Box
            sx={{
              m: 2,
            }}
          >
            <h2 style={{ fontWeight: 'bold' }} className="mb-4 text-lg">
              Does JotPsych make sense for me?
            </h2>
          </Box>
          <Divider />
          <Box
            sx={{
              m: 2,
              color: 'textColors.primaryText',
            }}
          >
            <Typography variant="body2" sx={{ pb: 1 }}>
              JotPsych saved me{' '}
              <b>
                {timeSavedSlider}
                {timeSavedSlider == 60 ? '+' : ''}
              </b>{' '}
              mins per note
            </Typography>
            <Slider
              value={timeSavedSlider}
              min={2}
              max={60}
              aria-label="Default"
              valueLabelDisplay="auto"
              onChange={(e, newValue: number | number[]) =>
                setTimeSavedSlider(Array.isArray(newValue) ? newValue[0] : newValue)
              } // Ensure newValue is a number
              sx={{ mb: 0, pb: 0, color: 'newDesignColors.eggplant900' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 0,
                paddingTop: 0,
              }}
            >
              <span>2 min</span>
              <span>60+ min</span>
            </div>
          </Box>
          <Box
            sx={{
              m: 2,
              mt: 4,
              color: 'textColors.primaryText',
            }}
          >
            <Typography variant="body2" sx={{ pb: 1 }}>
              During my trial I used JotPsych for <b>{patientEncounterPerct}%</b> of my patient
              encounters
            </Typography>
            <Slider
              value={patientEncounterPerct}
              min={0}
              max={100}
              aria-label="Default"
              valueLabelDisplay="auto"
              onChange={(e, newValue: number | number[]) =>
                setPatientEncounterPerct(Array.isArray(newValue) ? newValue[0] : newValue)
              } // Ensure newValue is a number
              sx={{ mb: 0, pb: 0, color: 'newDesignColors.eggplant900' }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 0,
                paddingTop: 0,
              }}
            >
              <span>0%</span>
              <span>100%</span>
            </div>
          </Box>
          <Box sx={{ m: 2, my: 4 }}>
            <div
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: 'rgb(28, 12, 84)' }}
            >
              <p className="text-3xl font-bold text-white">{calculateHoursSaved()} hrs per year</p>
              <p className="text-sm text-indigo-100">Estimated time saved per year with JotPsych</p>
            </div>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const ContactUsText = () => {
  const { brandLanguage } = useBrandLanguage();
  return (
    <div style={{ marginBottom: '20px' }}>
      If you believe you're seeing this message in error, or if you have questions or concerns,
      please{' '}
      <a
        href={`https://${brandLanguage.brandSite}/contact`}
        target="_blank"
        className="text-blue-500"
      >
        get in touch with us
      </a>
      .
    </div>
  );
};

interface SubscriptionButtonProps {
  message: string;
  onClick: () => void;
}
const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({ message, onClick }) => {
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        style={{ width: '100%', margin: 4 }}
        onClick={onClick}
      >
        {message}
      </Button>
    </div>
  );
};

interface LockoutHeaderTextProps {
  message: string;
}
const LockoutHeaderText: React.FC<LockoutHeaderTextProps> = ({ message }) => {
  return (
    <h3 style={{ fontWeight: 'bold' }} className="mb-4 text-lg">
      Action Required: {message}
    </h3>
  );
};
